import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { LalSegmentFields, type LalSegmentFormState } from '@openx/types/segments.ts';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber.ts';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas.ts';
import { MINIMUM_ESTIMATED_REACH } from '@openx/utils/services/config.ts';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSegmentContext } from '../../../utils/context/SegmentContext.tsx';
import { calculateSeverity } from './calculateSeverity.ts';
import { ReachInfoTooltip } from './ReachInfoTooltip.tsx';

const alertStyleOverrides = {
  alignItems: 'center',
  borderRadius: 0,
  maxHeight: 46,
  '&.MuiAlert-standard.MuiAlert-colorInfo': { backgroundColor: palette.background.boxDarker },
  '&.MuiAlert-standard.MuiAlert-colorInfo .MuiAlert-icon': { color: palette.text.secondary },
  '& .MuiAlert-message': { flex: 1 },
};

export const SeedEstimatedReach = () => {
  const { t } = useTranslation();
  const { useSegmentsEstimatedReachFetch } = useSegmentContext();
  const { setValue } = useFormContext<LalSegmentFormState>();

  const observableSeeds = useWatch({ name: LalSegmentFields.SEEDS });
  const observableEstimatedReach = useWatch({ name: LalSegmentFields.ESTIMATED_REACH });

  const isOnlyOneSeed = observableSeeds?.length === 1;

  const hasInvalidSeeds = observableSeeds?.some(seed => !seed.id || seed.status !== 'ready');

  const { estimatedReach: estimatedReachApi, loading } = useSegmentsEstimatedReachFetch(
    observableSeeds?.map(seed => seed.id),
    isOnlyOneSeed || hasInvalidSeeds
  );

  useEffect(() => {
    if (hasInvalidSeeds) {
      setValue(LalSegmentFields.ESTIMATED_REACH, 0, { shouldValidate: true });
    } else {
      setValue(LalSegmentFields.ESTIMATED_REACH, isOnlyOneSeed ? observableSeeds[0].reach : estimatedReachApi, {
        shouldValidate: true,
      });
    }

    return () => {
      setValue(LalSegmentFields.ESTIMATED_REACH, 0, { shouldValidate: true });
    };
  }, [estimatedReachApi, observableSeeds, setValue, isOnlyOneSeed, hasInvalidSeeds]);

  const alertSeverity = calculateSeverity(observableEstimatedReach, observableSeeds.length, loading);
  const formatedEstimatedReach = bigNumberWithCommas(observableEstimatedReach ?? 0);

  return (
    <Alert data-test={`seed-estimated-reach-${alertSeverity}`} severity={alertSeverity} sx={alertStyleOverrides}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 'fontWeightBold',
          }}
        >
          {t('Estimated Unique Seed Reach')}
        </Typography>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: 'center',
              color: palette.text.secondary,
            }}
          >
            <Typography
              color={palette.text.primary}
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: 'fontWeightBold',
              }}
            >
              {loading ? (
                <Loader noPadding size={16} data-test="seeds-estimated-reach-loader" />
              ) : (
                formatedEstimatedReach
              )}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: 12,
              }}
            >
              {t('of')} {abbreviatedNumber(MINIMUM_ESTIMATED_REACH)}
            </Typography>
          </Stack>

          <ReachInfoTooltip />
        </Stack>
      </Stack>
    </Alert>
  );
};
